import Colors from '../../utils/color';
import getStatusBarColor from '../../utils/getStatusBarColor';
import { useAppSelector } from '../../app/hooks';
import {
  selectAIStatus,
  selectIsAIEscalation,
  selectAIEscalationReason,
  selectIsAIEscalatedOnCarEnter,
  selectIsAIAutoMode,
  selectShowsTakeoverAlert,
} from '../../redux/features/ai/ai.selector';
import {
  AIStatusColors,
  DEFAULT_STATUS_MESSAGE,
} from '../../redux/features/ai/ai.constants';
import {
  selectIsHitl7Enabled,
  selectIsShadowModeEnabled,
} from '../../redux/features/config/config.selector';
import { selectHasAgentChosenIntercept } from '../../redux/features/agentMode/agentMode.selector';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { selectAIIndicator } from '../../selectors/restaurant';
import useIntervention from '../../hooks/intervention/useIntervention.hooks';

/**
 * HITL 7.0 band palette (HITL-1661).
 *
 * 3.0 gave the band its own greens, yellows and reds, chosen in isolation. Put
 * full width under the header, those sat next to the header indicators and the
 * intent buttons and read as three different design languages on one screen.
 * 7.0 drops the private palette and reuses the colours already in front of the
 * agent, so the same state means the same colour wherever they look:
 *
 *   state    background                        matches                text
 *   ───────  ────────────────────────────────  ─────────────────────  ──────────
 *   green    brightGreen  #70e000              header mic/wifi/car    near-black
 *   yellow   acknowledge  #FFC107              Conversation intents   near-black
 *   red      requestStaff #FF0048              the Staff button       white
 *
 * Staff intervention already used torchRed, which is the same value as
 * requestStaff, so it matched the Staff button before this change and is
 * untouched by it.
 *
 * The `orange` member of AIStatusColors has no mapping here for the same reason
 * getStatusBarColor's switch has no case for it — it falls through to green.
 * Car-enter escalation keeps its own orange identity separately, below.
 */
const HITL7_STATUS_PALETTE: Partial<
  Record<AIStatusColors, { background: string; textColor: string }>
> = {
  [AIStatusColors.green]: {
    background: Colors.brightGreen,
    textColor: Colors.primaryButtonTextColor,
  },
  [AIStatusColors.yellow]: {
    background: Colors.acknowledge,
    textColor: Colors.primaryButtonTextColor,
  },
  [AIStatusColors.red]: {
    background: Colors.requestStaff,
    textColor: Colors.white,
  },
};

const HITL7_FALLBACK = {
  background: Colors.brightGreen,
  textColor: Colors.primaryButtonTextColor,
};

export interface AIStatusBandState {
  /** False for pure-HITL restaurants — they never show an AI status band. */
  show: boolean;
  message: string;
  /** Ignored while `blink` is true; the animation owns the background then. */
  background: string;
  textColor: string;
  blink: boolean;
}

/**
 * The AI status band's state, headless.
 *
 * Extracted from CustomerPulse (HITL-1660) so the band can render in two
 * places without the precedence ladder being written twice: folded into the
 * Copilot card under hitl_3_0, and full width under the header under
 * hitl_7_0. The logic below is unchanged — only its home moved.
 *
 * ── Precedence (highest first), all gated on an ACTIVE SESSION ──────────────
 *
 *   condition                           band shows              colour
 *   ──────────────────────────────────  ──────────────────────  ──────────────
 *   staff intervention                  "Staff Intervention"    torch red
 *   agent intercepted (isAIAutoMode 0)  "Agent Intercept"       red (static)
 *   takeover requested, not yet taken   "take over now" (blink) red (pulsing)
 *   escalation (recoverable), pre-take  escalation reason       red / orange
 *   otherwise                           live AI status          green/yellow/red
 */
const useAIStatusBand = (): AIStatusBandState => {
  const aiStatus = useAppSelector(selectAIStatus);
  const isAIEscalation = useAppSelector(selectIsAIEscalation);
  const aiEscalationReason = useAppSelector(selectAIEscalationReason);
  const isAIEscalatedOnCarEnter = useAppSelector(selectIsAIEscalatedOnCarEnter);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const showsTakeoverAlert = useAppSelector(selectShowsTakeoverAlert);
  const sessionId = useAppSelector(selectSessionId);
  const aiEnabled = useAppSelector(selectAIIndicator);
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const isShadowMode = useAppSelector(selectIsShadowModeEnabled);
  const interceptArmedByAgent = useAppSelector(selectHasAgentChosenIntercept);
  const { isStaffInterventionHappening } = useIntervention();

  // ── Why the band gates on the SESSION, not the car ──────────────────────────
  // The AI control state is deliberately sticky — resetting it on car_exit caused
  // a prod incident (dropped order + cart revert). A drive-thru takeover lets the
  // car leave (car_exit) while the SAME session's order is still being finished,
  // so the band must follow an active session, NOT car presence:
  //
  //   signal            cleared on car_exit?          role
  //   ────────────────  ────────────────────────────  ─────────────────────────
  //   isAIAutoMode      no — sticky to session end     agent-intercepted flag
  //   requiresTakeover  no — sticky to session end     takeover alert
  //   isAIEscalation    no — sticky to session end     escalation flag
  //   sessionId         no while an order is pending   "is the session live?"
  //   isCarPresent      YES, immediately               (removed — too blunt)
  //
  // car_exit with a pending order only arms the post-car-exit timer; sessionId
  // clears only when the session TRULY ends (Finish/Cancel, final check, empty-
  // cart car_exit, kill switch, timeout). Gating on sessionId holds the intercept/
  // escalation status until the session ends, then idles to green.
  const sessionActive = Boolean(sessionId);

  // ── Interception & takeover ─────────────────────────────────────────────────
  // ANY agent interception (isAIAutoMode → false) during an active session shows
  // a static "Agent Intercept" — not just hard takeovers. A takeover escalation
  // ADDITIONALLY blinks a "take over now" alert while the AI is still in control,
  // before the agent acts:
  //
  //   state                                   band
  //   ──────────────────────────────────────  ───────────────────────────────
  //   takeover requested, not yet answered   BLINKS "take over now" (blink)
  //   agent intercepted (any escalation)      static "Agent Intercept"
  //
  // isAIAutoMode and requiresTakeover are both sticky, so these hold for the rest
  // of the session. The alert stays gated on the takeover flag + unresolved-items
  // feature; the plain "Agent Intercept" label does not.
  //
  // HITL-1650 moved that condition, staff intervention included, into
  // `selectShowsTakeoverAlert`. It is no longer only a display decision: the
  // console arms `Intercept` off the same predicate, and the band and the
  // auto-arm have to agree about what "the AI is asking for a human" means or
  // the console says one thing and does another.
  const agentIntercepted = !isAIAutoMode && sessionActive;
  const showEscalation = isAIEscalation && sessionActive;
  // Between sessions (no active session) the band idles on the default green —
  // the live aiStatus/escalation describe a conversation that is over.
  const displayedAIStatus = sessionActive
    ? aiStatus
    : { status: AIStatusColors.green, message: DEFAULT_STATUS_MESSAGE };

  // ── Pure-HITL parity ────────────────────────────────────────────────────────
  // The legacy AIStatusBar mounted only for AI restaurants (isAIActive), so a
  // pure-HITL restaurant never showed an AI status bar. Match that here:
  //
  //   restaurant    band?   staff-intervention cue
  //   ────────────  ──────  ──────────────────────────────
  //   AI enabled    yes     band + omnibar red border
  //   pure HITL     no      omnibar red border only
  //
  // ── Intercept clears the band under hitl_7_0 (HITL-1661) ────────────────────
  // Once an agent has taken the conversation, the band is narrating a state the
  // agent is now driving themselves. Under 3.0 it lingers as a static "Agent
  // Intercept" strip, competing for attention with nothing left to say; under
  // 7.0 it goes away, which is what the screen did before 3.0.
  //
  // Staff intervention is deliberately NOT swept up in this. The reasoning is
  // the mirror image of the intercept case: on intercept the agent is the one
  // driving, so the band is telling them something they already know, whereas
  // during a staff intervention the agent can do nothing at all about it. The
  // band is then their only notice that control has left their hands, so it
  // stays — even when they have also intercepted.
  //
  // selectAIIndicator is typed as the Boolean wrapper; coerce to a primitive
  // so callers get a plain boolean they can use in a `!show` guard.
  /**
   * WHAT AN INTERCEPTION SHOWS.
   *
   * HITL-1661 removed the whole band on intercept, reasoning that the agent is
   * now driving and does not need it narrated back at them. Two things were
   * wrong with that in practice:
   *
   *   1. It took the agent's BRIEF with it. When the takeover follows an
   *      escalation the band is carrying the reason they are now working on,
   *      and it vanished at the exact moment they picked up the work.
   *   2. It fired far more often than "a takeover". `isAIAutoMode` is cleared by
   *      EVERY interception type, `TTS_SENT` among them, so an agent who merely
   *      spoke during an escalation lost the band to a readback.
   *
   * And what it left behind was an empty strip above the panes, which reads as
   * something broken rather than as a state.
   *
   * So the band stays either way and only the WORDS change: the escalation
   * reason where there is one, otherwise the plain "Agent Intercept" that HITL
   * 3.0 has always shown. `show` goes back to the single question it asked
   * before 7.0 — does this restaurant have an AI at all.
   *
   * The reason holds until the order is finalized, where `resetControlState`
   * clears `isAIEscalation`, so the agent keeps the context for as long as they
   * are working the order that caused it.
   *
   * Staff intervention is untouched by any of this; it outranks intercept and
   * takes its own branch everywhere below.
   */
  const interceptKeepsEscalationReason =
    isShadowMode &&
    agentIntercepted &&
    !isStaffInterventionHappening &&
    showEscalation &&
    Boolean(aiEscalationReason);

  /**
   * Everything above is gated on `hitl_shadow_mode`, not on `hitl_7_0`, so with
   * supervised mode off this clear-out is exactly HITL-1661's and the band
   * behaves as it does in production today.
   *
   * The changes ARE a fix to 7.0 rather than a part of supervised mode, and
   * gating them this way means every 7.0 restaurant keeps the bug until the flag
   * reaches them. That is the deliberate trade: it keeps 7.3.0's blast radius
   * equal to its flag, so enabling supervised mode at one restaurant cannot
   * change the status band for the whole fleet. Widening it later is this one
   * gate, once supervised mode has proved out.
   */
  const clearedByIntercept =
    isHitl7 &&
    !isShadowMode &&
    agentIntercepted &&
    !isStaffInterventionHappening;

  const show = Boolean(aiEnabled) && !clearedByIntercept;

  // Staff intervention outranks every AI status: a human now owns the order, so
  // the band reads "Staff Intervention" (static, no blink). Otherwise prefer the
  // escalation reason when one is set, falling back to the live status message —
  // which displayedAIStatus.message already resolves to (the green default when
  // idle), so it is the single reliable fallback for every non-flagged state.
  const message = isStaffInterventionHappening
    ? 'Staff Intervention'
    : // eslint-disable-next-line no-nested-ternary
    interceptKeepsEscalationReason
    ? // The brief the agent intercepted on, not a restatement of the
      // interception itself — the mode switch beside the band already says who
      // is driving, so 'Agent Intercept' here would say it twice.
      aiEscalationReason
    : agentIntercepted
    ? 'Agent Intercept'
    : (showEscalation && aiEscalationReason) || displayedAIStatus.message;

  /**
   * The blink stops when the AGENT has answered the alert — and only then.
   *
   * Selecting a mode emits nothing, so `isAIAutoMode` is still true at that
   * point and the alert would otherwise keep flashing at someone who has already
   * responded, stopping only once they actually did something. Their own press
   * is the commitment, so there is nothing further to prompt for.
   *
   * HITL-1650 is why this asks WHOSE Intercept it is rather than just reading
   * the mode. When the console arms Intercept off this very alert, nobody has
   * answered anything yet — and a blink that switched itself off would hide the
   * request from the one person it is addressed to, leaving a silent console in
   * Intercept and an agent with no idea the AI had given up. Arming opens the
   * gates; the band goes on asking until the agent does something about it.
   *
   * `selectHasAgentChosenIntercept` is ANDed with the supervised-mode flag, so
   * with the flag off nothing can move the mode, this is provably false, and the
   * blink is exactly the condition it was before.
   *
   * Staff intervention is already inside `selectShowsTakeoverAlert` — the static
   * "hand-off" red is the resolved, unambiguous state there.
   */
  const blink = showsTakeoverAlert && !interceptArmedByAgent;

  // ── Band background / text colour (matches legacy AIStatusBar) ───────────────
  //   state                background          text
  //   ───────────────────  ──────────────────  ─────────────────────────────
  //   staff intervention   torch red           white
  //   agent intercepted    red (static)        white
  //   escalation / status  getStatusBarColor   white on red/orange, else inherit
  //
  // `blink` drives the vivid takeover animation instead of these colours;
  // agentIntercepted is a HITL 3.0-only static state (the legacy bar unmounts on
  // intercept) rendered in the same vivid escalation red.
  // Escalation is pulled out of getStatusBarColor here rather than passed into
  // it, so the 7.0 branch can swap the plain-escalation red without touching the
  // helper the legacy bar still shares. Flag off, every arm below resolves to
  // exactly what getStatusBarColor returned before.
  const hitl7 =
    HITL7_STATUS_PALETTE[displayedAIStatus.status] ?? HITL7_FALLBACK;

  let background: string;
  let textColor: string;
  if (isStaffInterventionHappening) {
    background = Colors.torchRed;
    textColor = Colors.white;
  } else if (agentIntercepted && !interceptKeepsEscalationReason) {
    // The plain "Agent Intercept" strip. Reachable in two worlds: HITL 3.0,
    // which keeps its own #FF0000, and supervised mode, which takes requestStaff
    // like every other red on that console — HITL-1661 retired the band's
    // private palette, and this arm was still on the old colour only because
    // under 7.0 it was unreachable.
    //
    // Keyed on isShadowMode rather than isHitl7 for that reason: with supervised
    // mode off and 7.0 on, `clearedByIntercept` means the band never renders
    // here at all, so isHitl7 would be describing a case that cannot happen.
    //
    // An intercepted band that IS keeping its reason deliberately falls through
    // to the escalation arm instead, so the strip holds the exact colour it had
    // a moment earlier and the only visible change is that it stops flashing.
    background = isShadowMode ? Colors.requestStaff : Colors.red;
    textColor = Colors.white;
  } else if (showEscalation) {
    // Car-enter escalation keeps its orange identity in both versions; it is a
    // distinct state, not a severity, and no button on screen shares it.
    background = isAIEscalatedOnCarEnter
      ? Colors.orange
      : (isHitl7 && Colors.requestStaff) || Colors.red;
    textColor = Colors.white;
  } else if (isHitl7) {
    background = hitl7.background;
    textColor = hitl7.textColor;
  } else {
    background = getStatusBarColor(false, false, displayedAIStatus.status);
    textColor = 'inherit';
  }

  return { show, message, background, textColor, blink };
};

export default useAIStatusBand;
